"use strict";

var _Object$defineProperty = require("@babel/runtime-corejs3/core-js-stable/object/define-property");
var _interopRequireDefault = require("@babel/runtime-corejs3/helpers/interopRequireDefault");
_Object$defineProperty(exports, "__esModule", {
  value: true
});
exports.convertToRgb = exports.checkColorContrast = exports.calculateRelativeLuminance = void 0;
exports.isServerSide = isServerSide;
var _map = _interopRequireDefault(require("@babel/runtime-corejs3/core-js-stable/instance/map"));
var _parseInt2 = _interopRequireDefault(require("@babel/runtime-corejs3/core-js-stable/parse-int"));
var _every = _interopRequireDefault(require("@babel/runtime-corejs3/core-js-stable/instance/every"));
var _isNan = _interopRequireDefault(require("@babel/runtime-corejs3/core-js-stable/number/is-nan"));
function isServerSide() {
  // On server side, `window` is not defined, so we can use that as a
  // hastey way of determining if the code being run is in a server env
  return typeof window === 'undefined';
}

/**
 * @typedef RGB Object representation of a color in the RGB colorspace
 * @property {number} r the red component of the color
 * @property {number} g the green component of the color
 * @property {number} b the blue component of the color
 */

/**
 * Converts a color string to rgb.
 * Handles various colorspaces as well as web color names.
 *
 * The use of `getComputedStyle` serializes all colorspaces to
 * sRGB values of the form `rgb()` or `rgba()`. This approach
 * accounts for both.
 *
 * See https://www.w3.org/TR/css-color-4/#serializing-sRGB-values.
 *
 * @param {string} color string representation of a color in any CSS colorspace
 * @returns {RGB} the object representation of a color in the RGB colorspace
 */
const convertToRgb = color => {
  const fallback = {
    r: 255,
    g: 255,
    b: 255
  };
  try {
    if (!isServerSide()) {
      var _context, _context2;
      const fakeDiv = document.createElement('div');
      fakeDiv.style.color = color;
      fakeDiv.style.display = 'none';
      document.body.appendChild(fakeDiv);
      const cs = window.getComputedStyle(fakeDiv);
      const pv = cs.getPropertyValue('color');
      document.body.removeChild(fakeDiv);
      const [r, g, b] = (0, _map.default)(_context = pv.split('(')[1].split(')')[0].split(',')).call(_context, v => (0, _parseInt2.default)(v));
      if ((0, _every.default)(_context2 = [r, g, b]).call(_context2, n => !(0, _isNan.default)(n))) {
        return {
          r,
          g,
          b
        };
      }
    }
    return fallback;
  } catch (e) {
    return fallback;
  }
};

/**
 * Calculates the relative luminance of a given color.
 *
 * Follows WCAG relative luminance calculation: https://www.w3.org/TR/WCAG21/#dfn-relative-luminance.
 *
 * @param {RGB} color the object representation of a color in the RGB colorspace
 * @returns {number} the relative luminance of the color
 **/
exports.convertToRgb = convertToRgb;
const calculateRelativeLuminance = _ref => {
  var _context3;
  let {
    r,
    g,
    b
  } = _ref;
  const [RsRGB, GsRGB, BsRGB] = (0, _map.default)(_context3 = [r, g, b]).call(_context3, function (v) {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return RsRGB * 0.2126 + GsRGB * 0.7152 + BsRGB * 0.0722;
};

/**
 * Checks whether the given foreground and background color combination
 * meets WCAG AA level color contrast guidelines.
 *
 * See https://www.w3.org/TR/WCAG21/#contrast-minimum.
 *
 * @param {string} foreground text color string
 * @param {string} background background color string
 * @returns {boolean} whether the color combination passes WCAG AA level color contrast guidelines
 */
exports.calculateRelativeLuminance = calculateRelativeLuminance;
const checkColorContrast = (foreground, background) => {
  const fRGB = convertToRgb(foreground);
  const bRGB = convertToRgb(background);
  const fLuminance = calculateRelativeLuminance(fRGB);
  const bLuminance = calculateRelativeLuminance(bRGB);
  const ratio = fLuminance > bLuminance ? (bLuminance + 0.05) / (fLuminance + 0.05) : (fLuminance + 0.05) / (bLuminance + 0.05);
  return ratio < 1 / 4.5;
};
exports.checkColorContrast = checkColorContrast;